import {
  CheckOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { Badge, Button, Dropdown, theme, Tooltip } from "antd";
import useGlobalModal from "components/GlobalModals/GlobalModalContext/useGlobalModal";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { EDIT_POST_BUTTON_ID } from "fixtures/elementIDs";
import useGetPostDetailsQuery from "hooks/useGetPostDetailsQuery";
import { useState } from "react";
import { FiBookmark } from "react-icons/fi";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import { integrationsGetter, selectUserInfo } from "store/user/userSlice";
import PostCommentsContainer from "../PostComments/PostCommentsContainer.tsx";
import useScheduleCustomTime from "./ScheduleCustomTime/useScheduleCustomTime.tsx";
import { getDropdownItems } from "./utils/footerUtils.tsx";

// Define action function types based on useUpdatePostActions return types
// (Assuming these are the correct signatures - adjust if necessary based on the hook)
type SavePostAction = (args: {
  post: Post;
  updateBody?: any;
  showMessage?: boolean;
}) => Promise<any>;
type SchedulePostAction = (args: {
  post: Post;
  scheduled_for?: string | null;
  showMessage?: boolean;
}) => Promise<any>;
type PublishPostAction = (post: Post) => Promise<any>;
type DiscardPostAction = (post: Post) => Promise<any>;

interface PostCardFooterProps {
  post: Post;
  openPostEditor: (forceDesignTab?: boolean) => void;
  disabled?: boolean;
  // New props from GenericPostCard
  isLoading: boolean;
  savePost: SavePostAction;
  schedulePost: SchedulePostAction;
  publishPost: PublishPostAction;
  discardPost: DiscardPostAction;
}

const PostCardFooter = ({
  post,
  openPostEditor,
  disabled,
  // Destructure new props
  isLoading,
  savePost,
  schedulePost,
  publishPost,
  discardPost,
}: PostCardFooterProps) => {
  const { hasPermission, hasSomePermissions } = useUserPermissions();
  const { isAnonymous } = useAppSelector(selectUserInfo);
  const { isAgencyMember, isAgencyOwner } = useAppSelector(
    (state) => state.user
  );
  const { openGlobalModal } = useGlobalModal();
  const connectedSocials = useAppSelector(integrationsGetter);
  const hasConnectedSocials = connectedSocials.length > 0;
  const { token } = theme.useToken();

  const { data: comments = [] } = useGetPostDetailsQuery(post.id, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    select: (data) => data.comments ?? [],
  });

  const { customTimeMenuItem, CustomTimeModal } = useScheduleCustomTime();
  const [showComments, setShowComments] = useState(false);

  const disableButtons = isLoading || disabled;

  const canPublishPosts = hasPermission("post_status_publish_write");
  const canEditPosts = hasSomePermissions([
    "caption_write",
    "post_design_write",
  ]);

  const postNowTooltip = hasConnectedSocials
    ? "This will override your scheduled publishing time and remove it from your scheduled posts"
    : "Please connect at least one social to start posting";

  const dropdownItems = getDropdownItems({
    post,
    canPublishPosts,
    hasConnectedSocials,
    customTimeMenuItem,
    schedulePost,
    publishPost,
  });

  const postActionWrapper = <T extends (args: any) => unknown>(
    actionCallback: T,
    args: Parameters<T>[0]
  ) => {
    if (isAnonymous) {
      openGlobalModal({
        modalName: "signup",
        modalData: { successCallback: () => actionCallback(args) },
      });
    } else {
      actionCallback(args);
    }
  };

  const hasPermissionToComment = hasPermission("comment_read");

  const unresolvedCommentCount =
    comments.filter((comment) => !comment.resolved).length ?? 0;

  const shouldShowCommentsButton =
    hasPermissionToComment &&
    post.status !== "NEW" &&
    (isAgencyMember || isAgencyOwner);

  return (
    <>
      <div className="flex justify-between px-2 py-2">
        <div className="flex items-center gap-1">
          {canEditPosts && (
            <Tooltip
              title={
                window.matchMedia("(pointer: coarse)").matches
                  ? ""
                  : "Edit post"
              }
            >
              <Button
                id={EDIT_POST_BUTTON_ID}
                className="icon-btn"
                type="text"
                icon={<EditOutlined />}
                disabled={disableButtons}
                onClick={() => openPostEditor(true)}
              />
            </Tooltip>
          )}
          {shouldShowCommentsButton && (
            <Tooltip
              title={
                unresolvedCommentCount > 0
                  ? "Unresolved comments"
                  : "View comments"
              }
            >
              <Badge
                styles={{ indicator: { pointerEvents: "none" } }}
                dot={unresolvedCommentCount > 0}
                offset={[-10, 10]}
                className="flex items-center justify-center"
              >
                <Button
                  icon={
                    <MessageOutlined
                      style={{
                        color: showComments ? token.colorPrimary : undefined,
                      }}
                    />
                  }
                  type="text"
                  onClick={() => setShowComments(!showComments)}
                  disabled={disableButtons}
                  className="icon-btn"
                />
              </Badge>
            </Tooltip>
          )}
          {hasPermission("post_status_draft_write") &&
            post.status !== "LIKED" && (
              <Tooltip title="Save to drafts">
                <Button
                  icon={<FiBookmark style={{ height: 20, width: 17 }} />}
                  type="text"
                  onClick={() =>
                    postActionWrapper(savePost, {
                      post,
                      updateBody: {
                        status: "LIKED",
                        should_render: false,
                        review_status: null,
                      },
                    })
                  }
                  disabled={disableButtons}
                  className="icon-btn"
                />
              </Tooltip>
            )}
          {hasPermission("post_status_trash_write") &&
            post.status !== "REJECTED" && (
              <Tooltip title="Delete post">
                <Button
                  onClick={() => postActionWrapper(discardPost, post)}
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={disableButtons}
                  className="icon-btn"
                />
              </Tooltip>
            )}
        </div>

        <div className="flex items-center">
          {hasPermission("post_status_schedule_write") ? (
            <Dropdown.Button
              className="w-fit [&_button]:flex [&_button]:items-center"
              icon={<DownOutlined />}
              menu={{
                items: dropdownItems,
              }}
              onClick={() => postActionWrapper(schedulePost, { post })}
              disabled={disableButtons}
            >
              Schedule
            </Dropdown.Button>
          ) : (
            canPublishPosts && (
              <Tooltip title={postNowTooltip}>
                <Button
                  disabled={!hasConnectedSocials || disableButtons}
                  icon={<CheckOutlined />}
                  onClick={() => postActionWrapper(publishPost, post)}
                >
                  Publish Now
                </Button>
              </Tooltip>
            )
          )}
        </div>
      </div>
      {showComments && <PostCommentsContainer post={post} readonly={false} />}
      <CustomTimeModal post={post} />
    </>
  );
};

export default PostCardFooter;
